import { cva, type VariantProps } from 'class-variance-authority';
import {
  Children,
  type ComponentProps,
  Fragment,
  type ReactElement,
} from 'react';
import { useIds } from '@/hooks/use-ids';
import { cn } from '@/lib/utils';

const stackVariants = cva('flex', {
  variants: {
    direction: {
      row: 'flex-row',
      column: 'flex-col',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    wrap: {
      true: 'flex-wrap',
    },
  },
  defaultVariants: {
    direction: 'column',
  },
});

type StackProps = {
  spacing?: number;
  divider?: ReactElement;
} & VariantProps<typeof stackVariants> &
  ComponentProps<'div'>;

export const Stack = ({
  children,
  direction,
  align,
  justify,
  wrap,
  spacing,
  divider,
  className,
  style,
  ...props
}: StackProps) => {
  const childArray = Children.toArray(children);
  const ids = useIds(childArray.length);

  const gap =
    spacing === undefined ? undefined : `calc(var(--spacing) * ${spacing})`;

  return (
    <div
      className={cn(
        stackVariants({ direction, align, justify, wrap }),
        className,
      )}
      style={{ gap, ...style }}
      {...props}
    >
      {divider
        ? childArray.map((child, index) => (
            <Fragment key={`stack-item-${ids[index]}`}>
              {child}
              {index < childArray.length - 1 && divider}
            </Fragment>
          ))
        : children}
    </div>
  );
};
